/* SHOWCASE — Template 4 preview (curated, artisanal, single collection).
 * Sample brand: Artisan Ceramics. Ivory + Terracotta. Gallery feel. */
import { useCatalogFonts } from "./_fonts";
import { PreviewRibbon } from "./LinesheetPreview";
import { ShoppingCart, Plus, ArrowUpRight, ArrowDown } from "lucide-react";

const HERO_IMG    = "https://images.pexels.com/photos/18373966/pexels-photo-18373966.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FEAT_IMG    = "https://images.unsplash.com/photo-1590605095243-072811dbe64c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxhcnRpc2FuJTIwY2VyYW1pYyUyMHBvdHRlcnl8ZW58MHx8fHwxNzgxODE4OTU1fDA&ixlib=rb-4.1.0&q=85";
const GRID_IMG    = "https://images.unsplash.com/photo-1597696929736-6d13bed8e6a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxhcnRpc2FuJTIwY2VyYW1pYyUyMHBvdHRlcnl8ZW58MHx8fHwxNzgxODE4OTU1fDA&ixlib=rb-4.1.0&q=85";

const COLORS = {
  bg:       "#FAF6F1",
  surface:  "#FFFFFF",
  primary:  "#C25E4B",
  text:     "#2D2422",
  muted:    "#8A7975",
  border:   "#EADFD2",
};

const FEATURED = {
  name: "The Hana Stoneware Bowl",
  story: "Hand-thrown and twice-fired in a small kiln on the outskirts of Mashiko. Each bowl is signed by Yuki, the maker, on the base.",
  tiers: [
    { qty: "Pack of 6",  price: 168 },
    { qty: "Pack of 12", price: 312 },
    { qty: "Pack of 24", price: 576 },
  ],
};

const PRODUCTS = [
  { name: "Stoneware Coffee Mug, Set of 4",      price: 88,  moq: 6,  img: GRID_IMG },
  { name: "Hand-thrown Cereal Bowl",              price: 28,  moq: 12, img: FEAT_IMG },
  { name: "Faceted Espresso Cup, Pair",           price: 46,  moq: 8,  img: GRID_IMG },
  { name: "Footed Serving Plate, 28cm",           price: 64,  moq: 6,  img: FEAT_IMG },
  { name: "Clay Vase, Tall Bottle Form",          price: 92,  moq: 4,  img: GRID_IMG },
  { name: "Ash-glazed Tea Pot, 600ml",            price: 124, moq: 4,  img: FEAT_IMG },
];

const PRESS = ["Cereal Magazine", "Kinfolk", "Sight Unseen", "The New York Times Style"];

export default function ShowcasePreview() {
  useCatalogFonts();
  return (
    <div style={{ background: COLORS.bg, color: COLORS.text, fontFamily: "'Outfit', sans-serif" }} data-testid="showcase-preview">
      <PreviewRibbon name="Showcase"/>

      {/* HERO — full bleed */}
      <section className="relative" style={{ height: "92vh", minHeight: 620 }}>
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, #FAF6F1cc 92%)" }}/>

        {/* Sticky brand bar overlay */}
        <div className="absolute top-9 inset-x-0 px-10 py-6 flex items-center justify-between" style={{ color: "#fff" }}>
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full grid place-items-center" style={{ background: "#ffffffcc", color: COLORS.primary, fontFamily: "'Bodoni Moda', serif", fontWeight: 700, fontSize: 20 }}>H</span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] opacity-80" style={{ fontFamily: "'JetBrains Mono', monospace" }}>Mashiko · Japan</div>
              <div className="text-sm" style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: "italic" }}>Hana Atelier · Ceramics</div>
            </div>
          </div>
          <button className="h-10 px-5 rounded-full text-sm inline-flex items-center gap-2" style={{ background: "#ffffffe6", color: COLORS.text }} data-testid="cart-btn">
            <ShoppingCart className="w-4 h-4"/>Cart · 0
          </button>
        </div>

        {/* HERO CONTENT */}
        <div className="absolute inset-0 flex items-end pb-24 px-10">
          <div className="max-w-5xl">
            <div className="text-[11px] uppercase tracking-[0.36em]" style={{ color: "#ffffffb3", fontFamily: "'JetBrains Mono', monospace" }}>
              Spring 2026 · Capsule no. 7
            </div>
            <h1 className="mt-6 tracking-tight leading-[0.92]" style={{ fontFamily: "'Bodoni Moda', serif", fontSize: "clamp(56px, 9vw, 140px)", fontWeight: 500, color: "#fff" }}>
              Hana <em style={{ fontWeight: 400 }}>Atelier.</em>
            </h1>
            <p className="mt-7 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "#ffffffd9" }}>
              Eighteen ceramic objects, hand-thrown in a kiln on the outskirts of Mashiko.
              Made for restaurants and homes that take pleasure in the slowness of a meal.
            </p>
            <div className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em]" style={{ color: "#ffffffb3", fontFamily: "'JetBrains Mono', monospace" }}>
              <ArrowDown className="w-3 h-3 animate-bounce"/>Explore the collection
            </div>
          </div>
        </div>
      </section>

      {/* PRESS STRIP */}
      <section className="border-y" style={{ borderColor: COLORS.border }}>
        <div className="max-w-6xl mx-auto px-10 py-7 flex items-center justify-between gap-8 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>
            As featured in
          </div>
          <div className="flex items-center gap-10 flex-wrap">
            {PRESS.map((p) => (
              <div key={p} className="text-base tracking-wide opacity-70" style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: "italic" }}>{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCT — launch-page feel */}
      <section className="max-w-6xl mx-auto px-10 py-32">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <img src={FEAT_IMG} alt={FEATURED.name} className="w-full rounded-sm shadow-2xl" style={{ aspectRatio: "4/5", objectFit: "cover" }}/>
            <span className="absolute -top-4 -right-4 px-4 py-2 text-[10px] uppercase tracking-[0.28em] rounded-full" style={{ background: COLORS.primary, color: "#fff", fontFamily: "'JetBrains Mono', monospace" }}>
              The chapter piece
            </span>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.36em]" style={{ color: COLORS.primary, fontFamily: "'JetBrains Mono', monospace" }}>
              Featured · No. 01 of 18
            </div>
            <h2 className="mt-6 tracking-tight leading-[1.02]" style={{ fontFamily: "'Bodoni Moda', serif", fontSize: "clamp(40px, 5.5vw, 68px)", fontWeight: 500, fontStyle: "italic" }}>
              {FEATURED.name}
            </h2>
            <p className="mt-7 text-base leading-relaxed" style={{ color: COLORS.muted, maxWidth: 480 }}>
              {FEATURED.story}
            </p>

            <div className="mt-10 rounded-2xl p-6" style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
              <div className="text-[10px] uppercase tracking-[0.28em] mb-4" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>Volume pricing</div>
              <div className="space-y-2.5">
                {FEATURED.tiers.map((t, i) => (
                  <div key={t.qty} className="flex items-center justify-between py-2.5 border-b last:border-b-0" style={{ borderColor: COLORS.border }}>
                    <div className="text-sm" style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: i === 0 ? "normal" : "italic" }}>{t.qty}</div>
                    <div className="text-base" style={{ fontFamily: "'Bodoni Moda', serif", fontWeight: 600 }}>${t.price}</div>
                  </div>
                ))}
              </div>
              <button className="mt-5 w-full h-14 rounded-full text-sm font-medium inline-flex items-center justify-center gap-2" style={{ background: COLORS.text, color: COLORS.bg }}>
                <Plus className="w-4 h-4"/>Add featured piece to order
              </button>
              <div className="text-[10.5px] mt-3 text-center" style={{ color: COLORS.muted }}>
                Ships from Mashiko within 14 days · MOQ 6 pieces
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2-COL GRID */}
      <section className="max-w-6xl mx-auto px-10 pb-32">
        <div className="flex items-end justify-between mb-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.36em]" style={{ color: COLORS.primary, fontFamily: "'JetBrains Mono', monospace" }}>The collection</div>
            <h2 className="mt-4 tracking-tight leading-[1.02]" style={{ fontFamily: "'Bodoni Moda', serif", fontSize: "clamp(40px, 5vw, 64px)", fontWeight: 500, fontStyle: "italic" }}>
              Six chapters more.
            </h2>
          </div>
          <div className="text-sm" style={{ color: COLORS.muted }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{PRODUCTS.length} pieces</span> available
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-x-12 gap-y-20">
          {PRODUCTS.map((p, i) => (
            <article key={p.name} className="group">
              <div className="overflow-hidden rounded-sm" style={{ aspectRatio: "5/6" }}>
                <img src={p.img} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"/>
              </div>
              <div className="mt-6 flex items-end justify-between gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: COLORS.muted, fontFamily: "'JetBrains Mono', monospace" }}>
                    No. {String(i + 2).padStart(2, "0")}
                  </div>
                  <h3 className="mt-2 text-2xl tracking-tight" style={{ fontFamily: "'Bodoni Moda', serif", fontStyle: "italic", fontWeight: 500 }}>
                    {p.name}
                  </h3>
                  <div className="mt-2 text-sm" style={{ color: COLORS.muted }}>MOQ {p.moq} · from <span style={{ fontFamily: "'Bodoni Moda', serif", color: COLORS.text }}>${p.price}</span></div>
                </div>
                <button className="shrink-0 w-12 h-12 rounded-full grid place-items-center transition-colors" style={{ background: COLORS.primary, color: "#fff" }}>
                  <Plus className="w-4 h-4"/>
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ABOUT BRAND — closing essay */}
      <section className="border-t" style={{ borderColor: COLORS.border }}>
        <div className="max-w-6xl mx-auto px-10 py-32 grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.36em]" style={{ color: COLORS.primary, fontFamily: "'JetBrains Mono', monospace" }}>
              Our story
            </div>
            <h2 className="mt-6 tracking-tight leading-[1.04]" style={{ fontFamily: "'Bodoni Moda', serif", fontSize: "clamp(40px, 5.5vw, 68px)", fontWeight: 500 }}>
              <em style={{ fontWeight: 400 }}>One maker.</em><br/>One kiln. One quiet view of the river.
            </h2>
            <p className="mt-7 text-base leading-relaxed" style={{ color: COLORS.muted, maxWidth: 520 }}>
              Hana Atelier began in 2017 when Yuki returned home to Mashiko after a decade in Brooklyn.
              She built her kiln by hand using brick from the village. Every piece you&apos;ll find here
              passed through her hands &mdash; from raw clay to the final ash glaze. We don&apos;t scale.
              We don&apos;t outsource. We just make what the season allows.
            </p>
            <a className="mt-9 inline-flex items-center gap-1.5 text-sm" style={{ color: COLORS.primary, fontFamily: "'Bodoni Moda', serif", fontStyle: "italic" }}>
              Read the full essay <ArrowUpRight className="w-3.5 h-3.5"/>
            </a>
          </div>
          <div className="rounded-sm overflow-hidden" style={{ aspectRatio: "4/5" }}>
            <img src={FEAT_IMG} alt="" className="w-full h-full object-cover"/>
          </div>
        </div>
      </section>
    </div>
  );
}
